// src/components/phase05/familyMeetingMinutes/FamilyMeetingMinutesPrint.jsx
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchFamilyMeetingMinute } from "../../../store/slices/familyMeetingMinutesSlice";
import logo from "../../../assets/images/logo-samplee.png";

export default function FamilyMeetingMinutesPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentFamilyMeetingMinute, isLoading, error } = useSelector(
    (state) => state.familyMeetingMinutes
  );

  /* ────────────────────── FETCH RECORD ────────────────────── */
  useEffect(() => {
    if (id) dispatch(fetchFamilyMeetingMinute(id));
  }, [dispatch, id]);

  /* ────────────────────── AUTO PRINT & CLOSE ────────────────────── */
  useEffect(() => {
    if (currentFamilyMeetingMinute) {
      const timer = setTimeout(() => window.print(), 500);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentFamilyMeetingMinute, navigate]);

  /* ────────────────────── STATES ────────────────────── */
  if (isLoading) return <div className="p-4 text-center">Loading…</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentFamilyMeetingMinute)
    return <div className="p-4 text-center">No record found.</div>;

  const d = currentFamilyMeetingMinute;

  // Format date
  const fmt = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";

  return (
    <>
      {/* ────────────────────── PRINT STYLES (Same as SanaNotes) ────────────────────── */}
      <style jsx global>{`
        * {
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
        }
        body { background: #fff !important; color: #000 !important; }

        .print-container { padding: 20px !important; position: relative !important; }

        .logo {
          position: absolute !important;
          top: 10px !important;
          left: 10px !important;
          z-index: 1000 !important;
        }
        .logo img { height: 60px !important; width: auto !important; }
        /* Header */
        .header h3 {
          font-size: 1.5em !important;
          font-weight: bold !important;
          text-align: center !important;
          border-bottom: 1.5px solid #383838 !important;
          padding-bottom: 15px !important;
          margin-bottom: 10px !important;
          color: #000000 !important;
        }

        
        .header h6 {
          font-size: 1.2em !important;
          text-align: center !important;
          margin-bottom: 5px !important;
          font-weight: semibold !important;
          color: #000000 !important;
        }

        .info-row {
          display: flex !important;
          justify-content: space-between !important;
          margin-bottom: 12px !important;
          font-size: 1rem !important;
        }
        .underline {
          border-bottom: 1px solid #000 !important;
          display: inline-block !important;
          width: auto !important;
          margin-left: 8px !important;
          padding: 0 4px !important;
          font-weight: bold !important;
        }

        .section {
          margin-top: 20px !important;
        }
        .section-label {
          font-weight: bold !important;
          font-size: 1rem !important;
          display: block !important;
          margin-bottom: 6px !important;
        }

        /* Table for Members with S.No. */
        .member-table {
          width: 100% !important;
          border-collapse: collapse !important;
          font-size: 0.95rem !important;
          margin-top: 6px !important;
        }
        .member-table th,
        .member-table td {
          border: 1px solid #000 !important;
          padding: 6px 8px !important;
          text-align: left !important;
        }
        .member-table th {
          background: #f8f9fa !important;
          font-weight: bold !important;
          text-align: center !important;
          width: 15% !important;
        }

        .text-box {
          border: 1px solid #000 !important;
          padding: 10px !important;
          min-height: 80px !important;
          font-size: 0.95rem !important;
          line-height: 1.5 !important;
        }


        @media print {
          .print-container { padding: 15px !important; }
        }
      `}</style>

      {/* ────────────────────── LOGO ────────────────────── */}
      <div className="logo">
        <img src={logo} alt="The Recovery House" />
      </div>

      {/* ────────────────────── MAIN CONTENT ────────────────────── */}
      <div className="print-container">
        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>FAMILY MEETING MINUTES</h6>
        </div>

        {/* ────────────────────── CLIENT INFO (2 per row) ────────────────────── */}
        <div style={{ marginTop: "20px" }}>
          <div className="info-row">
            <div>
              <span className="info-label">Client's Name:</span>
              <span className="underline">{d.client_name || ""}</span>
            </div>
            <div>
              <span className="info-label">Date:</span>
              <span className="underline">{fmt(d.date || d.meeting_date)}</span>
            </div>
          </div>

          <div className="info-row">
            <div>
              <span className="info-label">Report Written By:</span>
              <span className="underline">{d.report_written_by || ""}</span>
            </div>
            <div></div>
          </div>
        </div>

        {/* ────────────────────── FAMILY MEMBERS (with S.No.) ────────────────────── */}
        <div className="section">
          <span className="section-label">Family Member(s) Present:</span>
          <table className="member-table">
            <thead>
              <tr>
                <th>S. No.</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {d.family_members && d.family_members.length > 0 ? (
                d.family_members.map((m, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: "center", width: "10%" }}>{i + 1}</td>
                    <td style={{ textAlign: "center", width: "90%" }}>{m.name || ""}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} style={{ fontStyle: "italic", color: "#aaa" }}>
                    No family members recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ────────────────────── TEAM MEMBERS (with S.No.) ────────────────────── */}
        <div className="section">
          <span className="section-label">Team Member(s) Present:</span>
          <table className="member-table">
            <thead>
              <tr>
                <th>S. No.</th>
                <th>Name</th>
              </tr>
            </thead>
            <tbody>
              {d.team_members && d.team_members.length > 0 ? (
                d.team_members.map((m, i) => (
                  <tr key={i}>
                    <td style={{ textAlign: "center", width: "10%" }}>{i + 1}</td>
                    <td style={{ textAlign: "center", width: "90%" }}>{m.name || ""}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} style={{ fontStyle: "italic", color: "#aaa" }}>
                    No team members recorded.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ────────────────────── AGENDA ────────────────────── */}
        <div className="section">
          <span className="section-label">Agenda of the Meeting:</span>
          <div className="text-box" style={{ minHeight: "120px" }}>
            {d.agenda || <span style={{ color: "#aaa" }}>No agenda recorded.</span>}
          </div>
        </div>

        {/* ────────────────────── SUMMARY ────────────────────── */}
        <div className="section">
          <span className="section-label">Summary of the Discussion:</span>
          <div className="text-box" style={{ minHeight: "200px" }}>
            {d.summary || <span style={{ color: "#aaa" }}>No summary recorded.</span>}
          </div>
        </div>

        {/* ────────────────────── ACTION PLAN ────────────────────── */}
        <div className="section">
          <span className="section-label">Action Plan Agreed Upon:</span>
          <div className="text-box" style={{ minHeight: "180px" }}>
            {d.action_plan || <span style={{ color: "#aaa" }}>No action plan recorded.</span>}
          </div>
        </div>

      </div>
    </>
  );
}